import { clearToken, getToken } from './api.js';

export class SaasBrandResearchError extends Error {
  constructor(message, code = 'brand_research_failed', status = 0) {
    super(message);
    this.name = 'SaasBrandResearchError';
    this.code = code;
    this.status = status;
  }
}

function currentLoginHref() {
  return `/login?next=${encodeURIComponent(window.location.href)}`;
}

function parseFrame(frame) {
  const line = frame.split('\n').find((item) => item.startsWith('data:'));
  if (!line) return null;
  try {
    return JSON.parse(line.slice(5).trim());
  } catch {
    return null;
  }
}

export async function refreshSaasBrandResearch({
  companyId,
  brandId,
  url,
  onProgress,
} = {}) {
  const token = getToken();
  const response = await fetch('/api/saas/brand-research', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(companyId ? { 'x-company-id': companyId } : {}),
    },
    body: JSON.stringify({ brandId, url }),
  });
  if (response.status === 401) {
    clearToken();
    window.location.href = currentLoginHref();
    throw new SaasBrandResearchError('Sign in again to continue.', 'unauthorized', 401);
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new SaasBrandResearchError(
      'Website research could not be refreshed.',
      payload.error || 'brand_research_failed',
      response.status,
    );
  }
  if (!response.body) {
    throw new SaasBrandResearchError(
      'Website research did not return a result.',
      'brand_research_stream_unavailable',
      502,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let research = null;
  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    let boundary;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const event = parseFrame(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      if (!event) continue;
      if (event.type === 'progress') onProgress?.(event);
      if (event.type === 'error') {
        throw new SaasBrandResearchError(
          'Website research could not be refreshed.',
          event.code || 'brand_research_failed',
          502,
        );
      }
      if (event.type === 'result') research = event.research || null;
    }
    if (done) break;
  }
  if (!research?.id) {
    throw new SaasBrandResearchError(
      'Website research did not return a saved result.',
      'brand_research_result_missing',
      502,
    );
  }
  return research;
}
